import cloneDeep from "clone-deep"


export type ObjectState = {
    position: number[]
    momentum: number[]
}

export type EnergyGradientFunction = (objectIndex: number, position: number[], momentum: number[], integratorState: IntegratorState) => {
    gradPosition: number[],
    gradMomentum: number[]
}

export type IntegratorState = {
    time: number
    objects: ObjectState[]
}

export interface Integrator {
    iterate: (state: IntegratorState, dt: number) => IntegratorState
}

export type SymplecticIntegratorConstants = { c: number, d: number }[]

function vectorMulAddInline(a: number[], b: number[], f: number) {
    for (let i = 0; i < a.length; i++) {
        a[i] += f * b[i]
    }
}

function vectorMulAdd(result: number[], a: number[], b: number[], f: number) {
    for (let i = 0; i < a.length; i++) {
        result[i] = a[i] + f * b[i]
    }
}

export class SymplecticIntegrator implements Integrator {
    constructor(readonly constants: SymplecticIntegratorConstants,
        readonly getEnergyGradients: EnergyGradientFunction) {
    }

    public iterate = (state: IntegratorState, dt: number) => {
        const newState = cloneDeep(state)
        newState.time += dt
        dt /= 2

        const auxPosition: number[][] = []
        const auxMomentum: number[][] = []

        // 1. Iter

        for (let objectId = 0; objectId < newState.objects.length; objectId++) {
            const object = newState.objects[objectId]

            const { c, d } = this.constants[0]

            // Initialize aux position and momentum
            const auxPos: number[] = []
            const auxMom: number[] = []
            auxPos.length = object.position.length
            auxMom.length = object.momentum.length

            // Calculate gradients at real coords
            let grads = this.getEnergyGradients(objectId, object.position, object.momentum, state)
            vectorMulAdd(auxPos, object.position, grads.gradMomentum, dt * d)
            vectorMulAdd(auxMom, object.momentum, grads.gradPosition, dt * -d)

            // Calculate gradients at aux coords
            grads = this.getEnergyGradients(objectId, auxPos, auxMom, state)
            vectorMulAddInline(object.position, grads.gradMomentum, dt * c)
            vectorMulAddInline(object.momentum, grads.gradPosition, dt * -c)

            auxPosition.push(auxPos)
            auxMomentum.push(auxMom)
        }

        for (const { c, d } of this.constants.slice(1, this.constants.length - 1)) {
            for (let objectId = 0; objectId < newState.objects.length; objectId++) {
                const object = newState.objects[objectId]
                const auxPos = auxPosition[objectId]
                const auxMom = auxMomentum[objectId]

                // Calculate gradients at real coords
                let grads = this.getEnergyGradients(objectId, object.position, object.momentum, state)
                vectorMulAddInline(auxPos, grads.gradMomentum, dt * d)
                vectorMulAddInline(auxMom, grads.gradPosition, dt * -d)

                // Calculate gradients at aux coords
                grads = this.getEnergyGradients(objectId, auxPos, auxMom, state)
                vectorMulAddInline(object.position, grads.gradMomentum, dt * c)
                vectorMulAddInline(object.momentum, grads.gradPosition, dt * -c)
            }
        }

        for (let objectId = 0; objectId < newState.objects.length; objectId++) {
            const d = this.constants[this.constants.length - 1].d

            const object = newState.objects[objectId]
            const auxPos = auxPosition[objectId]

            // Calculate gradients at real coords
            const grads = this.getEnergyGradients(objectId, object.position, object.momentum, state)
            vectorMulAdd(object.position, auxPos, grads.gradMomentum, dt * d)
        }

        // 2. Iter
        for (let objectId = 0; objectId < newState.objects.length; objectId++) {
            const object = newState.objects[objectId]

            const { c, d } = this.constants[0]

            // Initialize aux position and momentum
            const auxPos = auxPosition[objectId]
            const auxMom = auxMomentum[objectId]

            // Calculate gradients at real coords
            let grads = this.getEnergyGradients(objectId, object.position, object.momentum, state)
            vectorMulAdd(auxPos, object.position, grads.gradMomentum, dt * d)
            vectorMulAdd(auxMom, object.momentum, grads.gradPosition, dt * -d)

            // Calculate gradients at aux coords
            grads = this.getEnergyGradients(objectId, auxPos, auxMom, state)
            vectorMulAddInline(object.position, grads.gradMomentum, dt * c)
            vectorMulAddInline(object.momentum, grads.gradPosition, dt * -c)
        }

        for (const { c, d } of this.constants.slice(1, this.constants.length - 1)) {
            for (let objectId = 0; objectId < newState.objects.length; objectId++) {
                const object = newState.objects[objectId]
                const auxPos = auxPosition[objectId]
                const auxMom = auxMomentum[objectId]

                // Calculate gradients at real coords
                let grads = this.getEnergyGradients(objectId, object.position, object.momentum, state)
                vectorMulAddInline(auxPos, grads.gradMomentum, dt * d)
                vectorMulAddInline(auxMom, grads.gradPosition, dt * -d)

                // Calculate gradients at aux coords
                grads = this.getEnergyGradients(objectId, auxPos, auxMom, state)
                vectorMulAddInline(object.position, grads.gradMomentum, dt * c)
                vectorMulAddInline(object.momentum, grads.gradPosition, dt * -c)
            }
        }

        for (let objectId = 0; objectId < newState.objects.length; objectId++) {
            const d = this.constants[this.constants.length - 1].d

            const object = newState.objects[objectId]
            const auxMom = auxMomentum[objectId]

            // Calculate gradients at real coords
            const grads = this.getEnergyGradients(objectId, object.position, object.momentum, state)
            vectorMulAdd(object.momentum, auxMom, grads.gradPosition, dt * -d)
        }

        return newState
    }
}